import { useEffect, useRef } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, Search, Zap, Activity, Radio } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StatCard } from '@/components/design-system/StatCard'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import TelemetryDateFilter from '../components/TelemetryDateFilter'
import ChannelBreakdownChart from '../components/ChannelBreakdownChart'
import SessionsTable from '../components/SessionsTable'
import { telemetryPathWithParams, telemetryRangeParams, useTelemetryNavigation, useSearchQuery } from '../hooks/useTelemetryNavigation'
import { formatNumber, statusBadge } from '../utils'
import TokensWithBreakdown from '../components/TokensWithBreakdown'
import DailyUsagePanel, { type DailyUsage } from './components/DailyUsagePanel'
import type { DateRange, Pagination, OrganizationRef, TokenBreakdown } from '../types'

interface Session {
  id: string
  sessionId: string
  label: string | null
  lastChannel: string | null
  model: string | null
  totalEvents: number
  totalTokens: number
  tokenBreakdown: TokenBreakdown | null
  startedAt: string | null
  lastEventAt: string | null
  matchSnippet: string | null
  matchRole: string | null
}

interface DigitalWorker {
  id: string
  name: string
  instanceName: string
  fleetProfile: string | null
  provisioningStatus: string | null
}

interface Stats {
  totalSessions: number
  totalTokens: number
  activeSessions: number
  tokenBreakdown: TokenBreakdown | null
}

interface ChannelBreakdownItem {
  channel: string
  sessions: number
}

interface Props {
  digitalWorker: DigitalWorker
  organization: OrganizationRef
  dailyUsage: DailyUsage
  stats: Stats
  sessions: Session[]
  pagination: Pagination
  search: string
  channel: string
  dateFrom: string
  dateTo: string
  dateRange: DateRange
  channels: string[]
  channelBreakdown: ChannelBreakdownItem[]
  sortBy: string
  sortDirection: 'asc' | 'desc'
}

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function buildRangeLabel(dateFrom: string, dateTo: string) {
  const from = dateFrom ? formatShortDate(dateFrom) : '...'
  const to = dateTo ? formatShortDate(dateTo) : 'hoje'
  if (!dateFrom && !dateTo) return 'todo o histórico'
  return `${from} → ${to}`
}

function DigitalWorkerShow({ digitalWorker, organization, dailyUsage, stats, sessions, pagination, search, channel, dateFrom, dateTo, dateRange, channels, channelBreakdown, sortBy, sortDirection }: Props) {
  const [query, setQuery] = useSearchQuery(search)
  const normalizingSortRef = useRef(false)
  const basePath = `/cc/telemetry/digital_workers/${digitalWorker.id}`
  const rangeParams = telemetryRangeParams(dateFrom, dateTo, dateRange)
  const { navigateWithParams, handleSort, handleDateChange } = useTelemetryNavigation(basePath, {
    search: search || undefined,
    channel: channel || undefined,
    ...rangeParams,
    sort_by: sortBy,
    sort_direction: sortDirection,
    page: pagination.page,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigateWithParams({ search: query || undefined, page: 1 })
  }

  useEffect(() => {
    if (sortBy !== 'total_cost') {
      normalizingSortRef.current = false
      return
    }
    if (normalizingSortRef.current) return

    normalizingSortRef.current = true
    const params = new URLSearchParams(window.location.search)
    params.set('sort_by', 'last_event_at')
    params.set('sort_direction', 'desc')
    params.set('page', '1')
    window.location.replace(`${basePath}?${params.toString()}`)
  }, [basePath, sortBy])

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <Link href={telemetryPathWithParams(`/cc/telemetry/organizations/${organization.slug}`, rangeParams)} className="text-muted-foreground hover:text-muted-foreground transition-colors"><ArrowLeft className="h-5 w-5" /></Link>
        <h2 className="text-2xl font-bold text-foreground">{digitalWorker.name}</h2>
      </div>
      <div className="flex items-center gap-2 ml-8 mb-4">
        <span className="font-mono text-sm text-muted-foreground">{digitalWorker.instanceName}</span>
        {digitalWorker.provisioningStatus && statusBadge(digitalWorker.provisioningStatus)}
        {digitalWorker.fleetProfile && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent text-muted-foreground">{digitalWorker.fleetProfile}</span>
        )}
      </div>

      <DailyUsagePanel dailyUsage={dailyUsage} />

      <div className="mb-4"><TelemetryDateFilter dateFrom={dateFrom} dateTo={dateTo} dateRange={dateRange} onChange={handleDateChange} /></div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
        <StatCard label="Sessões" value={formatNumber(stats.totalSessions)} icon={<Activity />} colorScheme="gray" />
        <StatCard label="Sessões Ativas" value={formatNumber(stats.activeSessions)} icon={<Radio />} colorScheme="green" />
        <StatCard
          label="Tokens (intervalo)"
          value={<TokensWithBreakdown total={stats.totalTokens} breakdown={stats.tokenBreakdown} />}
          icon={<Zap />}
          colorScheme="blue"
        />
        <ChannelBreakdownChart data={channelBreakdown} />
      </div>
      <p className="text-xs text-muted-foreground mb-6">
        Tokens do intervalo {buildRangeLabel(dateFrom, dateTo)}. Para uso diário, veja o painel no topo.
      </p>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">{pagination.total} sessões</span>
        <div className="flex items-center gap-2">
          {channels.length > 0 && (
            <select
              value={channel}
              onChange={(e) => navigateWithParams({ channel: e.target.value || undefined, page: 1 })}
              className="text-sm border border-border rounded-lg px-2 py-2 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Todos canais</option>
              {channels.map((ch) => (<option key={ch} value={ch}>{ch}</option>))}
            </select>
          )}
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por label, session ID ou conteúdo..." className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72" />
            </div>
            <Button type="submit" variant="outline" size="sm">Buscar</Button>
          </form>
        </div>
      </div>

      <SessionsTable
        sessions={sessions}
        digitalWorkerId={digitalWorker.id}
        pagination={pagination}
        sortBy={sortBy}
        sortDirection={sortDirection}
        searchQuery={query}
        onSort={(key) => handleSort(sortBy, sortDirection, key)}
        onPageChange={(page) => navigateWithParams({ page })}
      />
    </div>
  )
}

DigitalWorkerShow.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default DigitalWorkerShow
